import "../address.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { LuTags } from "react-icons/lu";
import { usePostDropOffProxyMutation } from "../services/slices/searchAddress";
import { MdCancel } from "react-icons/md";
import {
  DropOff,
  DetailedSuggestion,
  exportDetails,
  AddressDropDownParams,
} from "type/addressType";
import { DropoffDropDown } from "./DropoffDropDown";
import { DropDownPopup } from "./DropDownPopup";
import { TbAlertHexagon } from "react-icons/tb";
import {
  useAutocompleteSession,
  useGoogleAutocomplete,
} from "hooks/useGoogleAutoComplete";
import { TextInput } from "./common/form/Input";
import { debounce } from "lodash";
export default function AddressDropDown({
  setDropId,
  changeFn,
  addressKey = "dropOffAddress",
  masterState,
  showDropOff = true,
  showManualButton = true,
  setCurrentAddress,
  currentAddress,
  value,
  textName,
  onChange,
  placeholder,
  label,
}: AddressDropDownParams): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const [suggestions, setSuggestions] = useState<DetailedSuggestion[]>([]);
  const { autocompleteService, placesService } = useGoogleAutocomplete();
  const { getSessionToken, resetSession, isLoaded } = useAutocompleteSession();
  const [showModal, setShowModal] = useState(false);
  const [suggest, setSuggest] = useState<boolean | null>(false);
  const [dropOff, setDropOff] = useState<DropOff[] | null>(null);
  const [exportDetails, setExportDetails] = useState<exportDetails>({
    city: "",
    zipCode: "",
  });
  const [postDropOffProxy, { isLoading }] = usePostDropOffProxyMutation();
  const token = getSessionToken();

//  useEffect(() => {
//   if (
//     typeof addressKey === "object" &&
//     addressKey !== null &&
//     "addressKey" in addressKey &&
//     addressKey.addressKey === "pickUpAddress"
//   ) {
//     setSuggest(true);
//   } 
//   // eslint-disable-next-line
// }, []);

  const checkAddressKeyType = useMemo(() => {
    return (
      typeof addressKey === "object" &&
      "restrict" in addressKey &&
      addressKey.restrict === true
    );
  }, [addressKey]);

  const debouncedSearch = useMemo(
    () =>
      debounce((inputValue: string) => {
        if (!autocompleteService || !placesService || !isLoaded || !suggest) {
          setSuggestions([]);
          return;
        } else {
          autocompleteService.getPlacePredictions(
            {
              input: inputValue,
              componentRestrictions: checkAddressKeyType
                ? { country: "NG" }
                : undefined,
              sessionToken: token || undefined,
            },
            async (predictions, status) => {
              if (
                status !== google.maps.places.PlacesServiceStatus.OK ||
                !predictions
              ) {
                setSuggestions([]);
                return;
              }

              const detailPromises = predictions.map((prediction) => {
                return new Promise<DetailedSuggestion | null>((resolve) => {
                  placesService.getDetails(
                    { placeId: prediction.place_id },
                    (place, status) => {
                      if (
                        status === google.maps.places.PlacesServiceStatus.OK &&
                        place?.geometry?.location
                      ) {
                        const addressComponents =
                          place.address_components || [];

                        const getComponent = (type: string) =>
                          addressComponents.find((comp) =>
                            comp.types.includes(type)
                          )?.long_name || "";

                        resolve({
                          description: place.formatted_address || "",
                          place_id: prediction.place_id,
                          mainText: prediction.structured_formatting.main_text,
                          secondaryText:
                            prediction.structured_formatting.secondary_text,
                          location: {
                            lat: place.geometry.location.lat(),
                            lng: place.geometry.location.lng(),
                          },
                          country: getComponent("country"),
                          state: getComponent("administrative_area_level_1"),
                          houseNumber: getComponent("street_number"),
                        });
                        setExportDetails({
                          city: getComponent("locality"),
                          zipCode: getComponent("postal_code"),
                        });
                      } else {
                        resolve(null);
                      }
                    }
                  );
                });
              });

              const results = await Promise.all(detailPromises);
              setSuggestions(
                results.filter((r): r is DetailedSuggestion => !!r)
              );
            }
          );
        }
      }, 500),

    [
      autocompleteService,
      placesService,
      token,
      isLoaded,
      suggest,
      checkAddressKeyType,
    ]
  );

  useEffect(() => {
    if (value.length > 6 && suggest) {
      debouncedSearch(value);
    }
    return () => {
      debouncedSearch.cancel();
    };
  }, [value, suggest, debouncedSearch]);

 
  useEffect(() => {
    async function fetchDropOffs() {
      if (!showDropOff) return;
      const { address, latitude, longitude } = currentAddress;
      if (!address || !latitude || !longitude) {
        return;
      }

      try {
        const data = {
          address: currentAddress.address,
          longitude: currentAddress.longitude,
          latitude: currentAddress.latitude,
          houseNumber: currentAddress.houseNumber,
          country: "Nigeria",
        };

        const response = await postDropOffProxy(data);
        if ("data" in response) {
          setDropOff(response.data.data.data);
        } else {
          setDropOff([]);
        }
      } catch (error) {
        setDropOff([]);
      }
    }

    fetchDropOffs();
  }, [currentAddress, postDropOffProxy, showDropOff]);

  async function handleAddressClick(
    e?: React.MouseEvent<HTMLUListElement | HTMLButtonElement>,
    isButtonClick: boolean = false
  ) {
    let value: string | null;
    if (isButtonClick) {
      value = `${currentAddress.houseNumber}, ${
        currentAddress.address.includes("street")
          ? currentAddress.address
          : `${currentAddress.address} street`
      }, ${currentAddress.city}, ${currentAddress.state}`;

      if (setDropId) {
        setDropId((prev: any) => ({
          ...prev,
          pickUpAddress: value,
        }));
      }

      const dropoffData = {
        city: currentAddress.city,
        state: currentAddress.state,
        latitude: "",
        longitude: "",
        country: "Nigeria",
        // country: curAddress.country,
      };

      try {
        const response = await postDropOffProxy(dropoffData);
        if ("data" in response) {
          setDropOff(response.data.data.data);
        } else {
          setDropOff([]);
        }
      } catch (error) {
        console.error("Error posting drop off proxy:", error);
        setDropOff([]);
      }
    } else {
      const target = e?.target as HTMLElement;
      value = target?.innerText || null;
    }
    // when addressKey is a string
    if (typeof addressKey === "string") {
      const evt: React.ChangeEvent<HTMLInputElement> = {
        target: {
          name: addressKey,
          value,
        } as HTMLInputElement,
      } as React.ChangeEvent<HTMLInputElement>;

      changeFn(evt);
    }
    if (typeof addressKey === "object" && "addressKey" in addressKey) {
      const event: React.ChangeEvent<HTMLInputElement> = {
        target: {
          name: addressKey.addressKey,
          value,
        } as HTMLInputElement,
      } as React.ChangeEvent<HTMLInputElement>;

      changeFn(event);
    }
    // when addressKey is an array of strings
    if (Array.isArray(addressKey)) {
      addressKey.forEach((item: string) => {
        const event: React.ChangeEvent<HTMLInputElement> = {
          target: {
            name: item,
            value: item.toLowerCase().includes("city")
              ? exportDetails.city || currentAddress.city
              : item.toLowerCase().includes("zipcode")
              ? exportDetails.zipCode
              : value,
          } as HTMLInputElement,
        } as React.ChangeEvent<HTMLInputElement>;

        changeFn(event);
      });
    }
    resetSession();
    setSuggest(false);
    setShowModal(false);
  }

  return (
    <div className="w-full" ref={containerRef}>
      <TextInput
        type="text"
        label={label}
        isRequired
        name={textName}
        value={value}
        onChange={onChange}
        onBlur={() => setSuggest(true)}
        placeholder={placeholder}
      />
      {suggest && !showModal ? (
        <div>
          {suggestions && suggestions.length > 0 && (
            <ul
              className={`  bg-white shadow-md rounded-b-2xl pt-3  flex flex-col items-stretch gap-2 ${
                suggestions.length > 5 ? "h-72 overflow-y-scroll " : ""
              }`}
              onClick={handleAddressClick}
            >
              {suggestions.map((suggestion, idx) => (
                <li
                  className="text-sm border-b-2 p-2 hover:cursor-pointer"
                  key={idx + 1}
                  onClick={() =>
                    setCurrentAddress({
                      address: suggestion.description,
                      city: suggestion.mainText,
                      state: suggestion.state,
                      longitude: suggestion.location.lng.toString(),
                      latitude: suggestion.location.lat.toString(),
                      houseNumber: suggestion.houseNumber,
                      country: suggestion.country,
                    })
                  }
                >
                  {suggestion.description}
                </li>
              ))}
            </ul>
          )}
          {showManualButton && value.length > 6 && (
            <div
              className="text-base p-2 font-medium rounded-b-2xl hover:bg-gray-100 hover:cursor-pointer transition-all delay-300 flex flex-col mt-2"
              onClick={() => setShowModal(true)}
            >
              <span className="text-red-600 flex items-center gap-1">
                <TbAlertHexagon size={22} /> Please select your address from the
                suggestions or
              </span>
              <span className="underline">Enter manually</span>
            </div>
          )}
        </div>
      ) : suggest !== null &&
        !suggest &&
        !showModal &&
        dropOff &&
        dropOff.length > 0 &&
        showDropOff ? (
        <div className="mt-4 bg-gray-200 border-2 border-inputBorderGreen rounded-lg bg-opacity-40 px-4 pb-4 pt-7 relative">
          <MdCancel
            size={18}
            className="absolute top-2 left-3"
            onClick={() => setSuggest(null)}
          />

          <h2 className="flex items-center gap-4 font-medium">
            <span>
              <LuTags size={32} />
            </span>

            <span>
              Enjoy 20% off your delivery fee if you drop off at a location
              close to you!
            </span>
          </h2>

          <label htmlFor="location" className="mt-3 block">
            Select drop-off location
          </label>

          {dropOff !== null &&
            dropOff.length > 0 &&
            setDropId &&
            showDropOff && (
              <DropoffDropDown dropdown={dropOff} setDropId={setDropId} />
            )}
        </div>
      ) : showModal ? (
        <DropDownPopup
          setShowModal={setShowModal}
          showModal={showModal}
          setCurAddress={setCurrentAddress}
          curAddress={currentAddress}
          handleAddressClick={handleAddressClick}
          masterState={masterState}
          loadingAddressProxy={isLoading}
        />
      ) : (
        <></>
      )}
    </div>
  );
}
